<template>
  <div class="evaluation-view">
    <v-container fluid>
      <!-- Loading State -->
      <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 50vh">
        <div class="text-center">
          <v-progress-circular size="64" color="primary" indeterminate />
          <p class="text-h6 mt-4">Cargando evaluación...</p>
        </div>
      </div>

      <!-- Error State -->
      <v-alert v-else-if="error" type="error" class="ma-4">
        {{ error }}
        <template #append>
          <v-btn @click="handleError" variant="text" color="white"> Volver </v-btn>
        </template>
      </v-alert>

      <!-- Evaluation Completed -->
      <div v-else-if="isCompleted" class="text-center pa-8">
        <v-icon size="120" color="success" class="mb-6"> mdi-check-circle </v-icon>
        <h1 class="text-h3 mb-4">Evaluación finalizada</h1>
        <p class="text-h6 mb-6 text-medium-emphasis">
          Has completado todas las preguntas de la evaluación
        </p>

        <v-card class="mx-auto mb-6" max-width="500">
          <v-card-text>
            <div class="text-h6 mb-2">Resumen</div>
            <v-divider class="mb-4" />
            <div class="d-flex justify-space-between mb-2">
              <span>Total de preguntas:</span>
              <span
                ><strong>{{ evaluationSummary.totalQuestions }}</strong></span
              >
            </div>
            <div class="d-flex justify-space-between mb-2">
              <span>Respondidas:</span>
              <span
                ><strong>{{ evaluationSummary.answeredQuestions }}</strong></span
              >
            </div>
            <div class="d-flex justify-space-between">
              <span>Tiempo total:</span>
              <span
                ><strong>{{ formatTotalTime() }}</strong></span
              >
            </div>
          </v-card-text>
        </v-card>

        <v-btn
          color="primary"
          size="large"
          @click="returnToDashboard"
          :loading="finishingSession"
          :disabled="finishingSession"
          class="px-8"
        >
          <v-icon left>mdi-home</v-icon>
          {{ finishingSession ? 'Cerrando...' : 'Volver al inicio' }}
        </v-btn>
      </div>

      <!-- Answer Result -->
      <div v-else-if="showAnswerResult && answerResultData">
        <AnswerResult
          :answer-data="answerResultData"
          :time-spent="answerTimeSpent"
          :quiz-revealed="quizRevealed"
          @continue="handleContinueAfterResult"
        />
      </div>

      <!-- Active Evaluation -->
      <div v-else-if="isActive && currentQuestion">
        <!-- Header with progress -->
        <v-card class="mb-4">
          <v-card-title class="d-flex align-center justify-space-between pa-4">
            <div>
              <h2 class="text-h5">{{ accessData?.group_name || 'Evaluación' }}</h2>
              <p class="text-subtitle-2 text-medium-emphasis mb-0">
                Pregunta {{ currentQuestionIndex + 1 }} de {{ totalQuestions }}
              </p>
            </div>
            <div class="text-center">
              <v-chip
                :color="timeRemaining <= 10 ? 'error' : timeRemaining <= 30 ? 'warning' : 'primary'"
                size="large"
                class="font-weight-bold"
              >
                <v-icon left>mdi-timer</v-icon>
                {{ formatTime(timeRemaining) }}
              </v-chip>
            </div>
          </v-card-title>

          <v-card-text class="pt-0">
            <v-progress-linear
              :model-value="timerProgress"
              :color="timeRemaining <= 10 ? 'error' : timeRemaining <= 30 ? 'warning' : 'primary'"
              height="8"
              rounded
              class="mb-2"
            />
            <div class="d-flex justify-space-between text-caption">
              <span>Progreso del tiempo: {{ Math.round(timerProgress) }}%</span>
              <span v-if="currentQuestion.time_limit">
                Tiempo límite: {{ currentQuestion.time_limit }}s
              </span>
            </div>
          </v-card-text>
        </v-card>

        <!-- Question Content -->
        <EvaluationQuestion
          :question="currentQuestion"
          :time-remaining="timeRemaining"
          @answer-selected="handleAnswerSelected"
        />
      </div>

      <!-- No Questions Available -->
      <v-alert v-else type="warning" class="ma-4">
        No hay preguntas disponibles para esta evaluación.
        <template #append>
          <v-btn
            @click="returnToDashboard"
            variant="text"
            color="white"
            :loading="finishingSession"
            :disabled="finishingSession"
          >
            {{ finishingSession ? 'Cerrando...' : 'Volver' }}
          </v-btn>
        </template>
      </v-alert>
    </v-container>

    <!-- Error/Success Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" location="top" timeout="5000">
      {{ snackbarMessage }}
      <template #actions>
        <v-btn color="white" variant="text" @click="showSnackbar = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import EvaluationQuestion from '@/components/evaluation/EvaluationQuestion.vue'
import AnswerResult from '@/components/evaluation/AnswerResult.vue'

const route = useRoute()
const router = useRouter()
const store = useStore()

const exitDialog = ref(false)
const finishingSession = ref(false)
const showSnackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('error')
const showAnswerResult = ref(false)
const answerResultData = ref(null)
const answerTimeSpent = ref(0)
const quizRevealed = ref(false)

// Computed properties from store
const loading = computed(() => store.getters['evaluation/isLoading'])
const error = computed(() => store.getters['evaluation/getError'])
const isActive = computed(() => store.getters['evaluation/isEvaluationActive'])
const isCompleted = computed(() => store.getters['evaluation/isEvaluationCompleted'])
const currentQuestion = computed(() => store.getters['evaluation/getCurrentQuestion'])
const currentQuestionIndex = computed(() => store.getters['evaluation/getCurrentQuestionIndex'])
const totalQuestions = computed(() => store.getters['evaluation/getTotalQuestions'])
const timeRemaining = computed(() => store.getters['evaluation/getTimeRemaining'])
const evaluationProgress = computed(() => store.getters['evaluation/getEvaluationProgress'])
const timerProgress = computed(() => store.getters['evaluation/getTimerProgress'])
const accessData = computed(() => store.getters['evaluation/getAccessData'])
const evaluationSummary = computed(() => store.getters['evaluation/getEvaluationSummary'])
const timeoutResult = computed(() => store.getters['evaluation/getTimeoutResult'])
const sessionStatus = computed(() => store.getters['session/getSessionStatus'])

// Get data from route (only groupId, no sensitive data in URL)
const groupId = computed(() => route.params.groupId)

// --- Polling de respaldo para revelar resultados ---
let resultPollingInterval = null

const stopResultPolling = () => {
  if (resultPollingInterval) {
    clearInterval(resultPollingInterval)
    resultPollingInterval = null
  }
}

const checkSessionStatusForReveal = async () => {
  if (quizRevealed.value) {
    stopResultPolling()
    return
  }

  // Si el store ya tiene 'completed' (p.ej. llegó por WebSocket), revelar inmediatamente
  if (sessionStatus.value === 'completed') {
    quizRevealed.value = true
    stopResultPolling()
    return
  }

  // Consultar al backend (actualiza el store → dispara el watch de sessionStatus)
  const pinCode = store.getters['session/getPinCode']
  if (pinCode) {
    await store.dispatch('session/checkSessionStatus')
  }
}

const startResultPolling = () => {
  // Verificar inmediatamente (por si el WebSocket ya actualizó el store)
  checkSessionStatusForReveal()
  // Luego cada 3 segundos como respaldo
  resultPollingInterval = setInterval(checkSessionStatusForReveal, 3000)
}
// ------------------------------------------------

// Revelar resultados cuando el docente cierra el quiz (WebSocket o polling)
watch(sessionStatus, (newStatus) => {
  if (newStatus === 'completed' && showAnswerResult.value) {
    quizRevealed.value = true
    stopResultPolling()
  }
})

// Watch for timeout from store timer
watch(timeoutResult, async (result) => {
  if (result && result.success) {
    console.log('Timeout result received:', result)
    console.log('Answer data:', result.data)

    // Store timeout result data from backend
    answerResultData.value = result.data
    answerTimeSpent.value = result.timeSpent

    // Show answer result screen
    showAnswerResult.value = true

    // Clear timeout result
    store.commit('evaluation/CLEAR_TIMEOUT_RESULT')

    // Finish session immediately after timeout
    try {
      const sessionResult = await store.dispatch('session/finishSession')
      if (!sessionResult.success) {
        console.warn('Error finishing session automatically:', sessionResult.error)
      }
    } catch (error) {
      console.warn('Error finishing session automatically:', error)
    }

    // Iniciar polling para detectar cuando el docente revela resultados
    startResultPolling()
  }
})

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const formatTotalTime = () => {
  if (!evaluationSummary.value.startTime) return 'N/D'

  const totalSeconds = Math.floor((new Date() - new Date(evaluationSummary.value.startTime)) / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

const handleAnswerSelected = async (selectedOption) => {
  // Only handle explicit user submissions (not timeout)
  const result = await store.dispatch('evaluation/submitAnswer', selectedOption)

  if (result.success && result.data) {
    // Store answer result data from backend
    answerResultData.value = result.data
    answerTimeSpent.value = result.timeSpent

    // Show answer result screen
    showAnswerResult.value = true

    // Finish session immediately after answer
    try {
      const sessionResult = await store.dispatch('session/finishSession')
      if (!sessionResult.success) {
        console.warn('Error finishing session automatically:', sessionResult.error)
      }
    } catch (error) {
      console.warn('Error finishing session automatically:', error)
    }

    // Iniciar polling para detectar cuando el docente revela resultados
    startResultPolling()
  } else {
    // Show specific error message from backend
    const errorMsg = result.error || 'Error al enviar la respuesta'
    showError(errorMsg)

    setTimeout(() => {
      router.push('/pin-access')
    }, 3000)
  }
}

const handleContinueAfterResult = async () => {
  stopResultPolling()

  // Clean up and navigate to pin access
  showAnswerResult.value = false
  answerResultData.value = null
  quizRevealed.value = false

  store.dispatch('evaluation/resetEvaluation')
  store.dispatch('pins/clearAccessData')
  store.dispatch('session/clearSession')

  router.push('/pin-access')
}

const handleError = () => {
  router.push('/pin-access')
}

const returnToDashboard = async () => {
  finishingSession.value = true

  try {
    store.dispatch('evaluation/resetEvaluation')
    store.dispatch('pins/clearAccessData')
    store.dispatch('session/clearSession')

    // Navigate to pin access for students
    router.push('/pin-access')
  } catch (error) {
    console.error('Error cleaning up session:', error)
    showError('Error inesperado al limpiar la sesión')
  } finally {
    finishingSession.value = false
  }
}

const showError = (message) => {
  snackbarMessage.value = message
  snackbarColor.value = 'error'
  showSnackbar.value = true
}

const showSuccess = (message) => {
  snackbarMessage.value = message
  snackbarColor.value = 'success'
  showSnackbar.value = true
}

const initializeEvaluation = async () => {
  // Get access data from pins store only (no URL params)
  const evaluationAccessData = store.getters['pins/getAccessData']

  if (!evaluationAccessData || !groupId.value) {
    store.commit(
      'evaluation/SET_ERROR',
      'No se encontraron datos de acceso válidos. Por favor, valide el PIN nuevamente.',
    )
    return
  }

  // Verify the groupId matches the access data
  if (evaluationAccessData.group_id != groupId.value) {
    store.commit(
      'evaluation/SET_ERROR',
      'Los datos de acceso no coinciden con el grupo solicitado.',
    )
    return
  }

  // Start the evaluation
  const result = await store.dispatch('evaluation/startEvaluation', {
    groupId: groupId.value,
    accessData: evaluationAccessData,
  })

  if (!result.success) {
    console.error('Failed to start evaluation:', result.error)
  }
}

// Handle page reload/close
const handleBeforeUnload = (e) => {
  if (isActive.value) {
    e.preventDefault()
    e.returnValue = '¿Estás seguro que deseas salir de la evaluación?'
  }
}

// Prevenir capturas de pantalla
const preventScreenCapture = () => {
  const handleKeyDown = (e) => {
    if (e.key === 'PrintScreen') {
      e.preventDefault()
      showError('Las capturas de pantalla están deshabilitadas durante la evaluación')
      navigator.clipboard.writeText('')
      return false
    }

    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 's') {
      e.preventDefault()
      showError('Las capturas de pantalla están deshabilitadas durante la evaluación')
      return false
    }

    if (e.key === 'Meta' && e.shiftKey) {
      showError('Las capturas de pantalla están deshabilitadas durante la evaluación')
    }

    if (e.key === 'F12') {
      e.preventDefault()
      showError('Las herramientas de desarrollador están deshabilitadas durante la evaluación')
      return false
    }

    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'i') {
      e.preventDefault()
      showError('Las herramientas de desarrollador están deshabilitadas durante la evaluación')
      return false
    }

    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'c') {
      e.preventDefault()
      showError('Las herramientas de desarrollador están deshabilitadas durante la evaluación')
      return false
    }
  }

  const handleContextMenu = (e) => {
    e.preventDefault()
    showError('El clic derecho está deshabilitado durante la evaluación')
    return false
  }

  const handleVisibilityChange = () => {
    if (document.hidden && isActive.value) {
      showError('Has salido de la pestaña de evaluación. Esta acción ha sido registrada.')
      console.warn('Usuario salió de la pestaña durante la evaluación')
    }
  }

  const handleBlur = () => {
    if (isActive.value) {
      console.warn('Usuario cambió de ventana durante la evaluación')
    }
  }

  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('keyup', handleKeyDown)
  document.addEventListener('contextmenu', handleContextMenu)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('blur', handleBlur)

  return () => {
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('keyup', handleKeyDown)
    document.removeEventListener('contextmenu', handleContextMenu)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('blur', handleBlur)
  }
}

let cleanupScreenCapture = null

onMounted(() => {
  console.log('EvaluationView mounted with groupId:', groupId.value)

  initializeEvaluation()

  // Add beforeunload listener
  window.addEventListener('beforeunload', handleBeforeUnload)

  // Activar prevención de capturas de pantalla
  cleanupScreenCapture = preventScreenCapture()
})

onUnmounted(() => {
  stopResultPolling()

  // Clean up timer when component unmounts
  store.dispatch('evaluation/resetEvaluation')

  // Remove beforeunload listener
  window.removeEventListener('beforeunload', handleBeforeUnload)

  // Limpiar prevención de capturas de pantalla
  if (cleanupScreenCapture) {
    cleanupScreenCapture()
  }
})
</script>

<style scoped>
.evaluation-view {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  /* Prevenir selección de texto (dificulta capturas con OCR) */
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  /* Deshabilitar menú contextual */
  -webkit-touch-callout: none;
}

.exit-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
}

.v-progress-linear {
  border-radius: 4px;
}

.v-card {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
}

/* Adicional: Prevenir arrastrar imágenes */
.evaluation-view img {
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
}

/* Marca de agua sutil (opcional) */
.evaluation-view::after {
  content: attr(data-watermark);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-45deg);
  font-size: 6rem;
  color: rgba(0, 0, 0, 0.02);
  pointer-events: none;
  z-index: 9999;
  white-space: nowrap;
}
</style>
